import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputText } from "primereact/inputtext"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"
import { MultiSelect } from "primereact/multiselect"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import { gql, useQuery, useMutation } from "@apollo/client"
import { useParams, Link } from "react-router-dom"
import { toast } from "react-toastify"

export default function PPRoviderSummaryEntries() {
    const [t] = useTranslation()
    const [globalFilter, setGlobalFilter] = useState(null)
    const [selectedShops, setSelectedShops] = useState(null)

    let { providerId } = useParams()

    const [lazyParams, setLazyParams] = useState({
        providerId: providerId !== undefined ? providerId : null,
        first: 0,
        rows: 10,
        sortField: null,
        sortOrder: 1,
        filters: null
    })
    let findActiveSearchFilter = (obj) => {
        let results = []
        for (const property in obj) {
            if (obj[property].value !== null && obj[property].field !== "global") {
                results.push({
                    field: property,
                    value: obj[property]?.value,
                    mode: obj[property]?.matchMode
                })
            }
        }
        return results
    }

    let getCurrentSearchFilters = (globalFilter, filters) => {
        let searchFilter = []
        if (globalFilter != null && globalFilter != "") {
            searchFilter.push({
                field: "global_summary_entries",
                mode: "global_summary_entries",
                value: globalFilter
            })
        } else {
            searchFilter = findActiveSearchFilter(filters)
        }

        if (selectedShops != null && selectedShops.length > 0) {
            searchFilter.push({
                field: "shopName",
                mode: "equals",
                value: selectedShops.join(",")
            })
        }

        if (globalFilter === "") {
            searchFilter.length = 0
        }

        return searchFilter
    }

    let variables = {
        providerId: providerId,
        searchFilters: getCurrentSearchFilters(globalFilter, lazyParams.filters),
        page: Math.ceil(lazyParams.first / lazyParams.rows) + 1,
        limit: lazyParams.rows,
        sortField: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder
    }

    const LOAD_QUERY = gql`
        query providerSummaryEntries(
            $providerId: String
            $searchFilters: [DatatableSearchFilterInput]
            $sortField: String
            $sortOrder: Int!
            $limit: Int!
            $page: Int!
        ) {
            providerSummaryEntries(
                providerId: $providerId
                searchFilters: $searchFilters
                sortField: $sortField
                sortOrder: $sortOrder
                limit: $limit
                page: $page
            ) {
                providerSummariesEntries {
                    fromProductNr
                    customerId
                    ean
                    ownPrice
                    ownTotalPrice
                    productName
                    foreignPrice
                    foreignTotalPrice
                    shopName
                    providerName
                    location
                    priceDifference
                    createDate
                    providerId
                    stockString
                }
                total
                currentPage
            }

            distinctShopNames
        }
    `

    const PROVIDER_SUMMARY_ENTRIES_EXPORT_QUERY = gql`
        mutation startProviderSummaryEntriesExport(
            $searchFilters: [DatatableSearchFilterInput]
            $sortField: String
            $sortOrder: Int!
            $limit: Int!
        ) {
            startProviderSummaryEntriesExport(
                searchFilters: $searchFilters
                sortField: $sortField
                sortOrder: $sortOrder
                limit: $limit
            ) {
                responseCode
                responseMessage
                downloadUrl
            }
        }
    `

    const { loading, data, refetch } = useQuery(LOAD_QUERY, {
        skip: false,
        displayName: "ProviderSummaryEntriesQuery",
        variables: variables,
        fetchPolicy: "no-cache"
    })
    const totalRecords = data?.providerSummaryEntries?.total ?? 0
    const shops = data?.distinctShopNames ?? []

    useEffect(() => {
        loadLazyData()
    }, [lazyParams])

    const loadLazyData = () => {
        refetch()
    }

    const onLazyLoad = (event) => {
        setLazyParams(event)
    }

    const onUpdateFilter = (event) => {
        event.first = 0
        setLazyParams(event)
    }

    const shopFilter = (
        <MultiSelect
            value={selectedShops}
            label={t("provider_summary.shops")}
            options={shops}
            onChange={(e) => setSelectedShops(e.value)}
            placeholder={t("provider_summary.choose_shops")}
            display="chip"
            width={"100px"}
            emptyMessage={t("provider_summary.no_data_received")}
        />
    )

    const productNameBodyTemplate = (rowData) => {
        return (
            <Link to={`${rowData.location}`} target="_blank" rel="noopener noreferrer">
                {rowData.productName}
            </Link>
        )
    }

    const skuBodyTemplate = (rowData) => {
        return <Link to={`/pricetable/s/${rowData.fromProductNr.replace(/\//g, '||')}`}>{rowData.fromProductNr}</Link>
    }

    const priceDifferenceTemplate = (rowData) => {
        let className = "green"
        if (rowData.priceDifference < 0) {
            className = "red"
        } else if (rowData.priceDifference === 0) {
            className = ""
        }
        return <span className={className}>{rowData.priceDifference}</span>
    }

    const renderEuro = (amount) => {
        return new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR"
        }).format(amount)
    }

    const renderPrice = (rowData, property) => {
        return <span>{renderEuro(rowData[property])}</span>
    }

    const getExportVariables = () => {
        let variablesExport = { ...variables }
        variablesExport.limit = totalRecords
        return variablesExport
    }

    const [triggerProviderSummaryEntriesExport] = useMutation(PROVIDER_SUMMARY_ENTRIES_EXPORT_QUERY, {
        variables: getExportVariables(),
        fetchPolicy: "no-cache",
        onCompleted: () => {
            toast(t("export.started"))
        },
        onError: (error2) => {
            if (error2.message.toString().length > 0) {
                toast.error(t("export.start_failed"))
            }
        }
    })
    
    return (
        <>
            <div className="datatable-container">
                <div className="pp-toolbar">
                    <div className="pp-toolbar__group">
                        <span>
                            {t("provider_summary.count_records")}: {data?.providerSummaryEntries?.total ?? 0}
                        </span>
                        <IconField iconPosition="left">
                            <InputIcon className="fas fa-search" />
                            <InputText
                                className="p-datatable-global-search"
                                type="search"
                                onInput={(e) => setGlobalFilter(e.target.value)}
                                placeholder="Suche"
                            />
                        </IconField>
                    </div>
                    <button
                        id="buttonProviderSummaryExport"
                        className="btn btn-outline-primary"
                        type="button"
                        aria-expanded="false"
                        aria-controls="exportProviderSummary"
                        data-bs-toggle="modal"
                        data-bs-target="#createProviderSummaryEntriesExportDialog"
                        disabled={totalRecords == 0}>
                        <i className="fas fa-file-excel" /> {t("single.export")}
                    </button>
                </div>

                <DataTable
                    className="text-sm p-datatable-custom"
                    stripedRows
                    value={data?.providerSummaryEntries?.providerSummariesEntries}
                    lazy
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows}
                    totalRecords={totalRecords}
                    onPage={onLazyLoad}
                    onSort={onLazyLoad}
                    onFilter={onUpdateFilter}
                    sortField={lazyParams.sortField}
                    sortOrder={lazyParams.sortOrder}
                    filters={lazyParams.filters}
                    filterDelay={500}
                    globalFilter={globalFilter}
                    loading={loading}
                    filterDisplay="row"
                    globalFilterFields={["fromProductnr", "provider_name", "shopName"]}
                    emptyMessage={t("provider_summary.no_data_received")}
                    rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}>
                    <Column
                        field="fromProductNr"
                        header={t("provider_summary.column_fromProductnr")}
                        sortable
                        body={skuBodyTemplate}
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_fromProductnr_filter")}></Column>
                    <Column
                        field="ean"
                        header={t("provider_summary.column_gtin")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_gtin_filter")}></Column>
                    <Column
                        field="productName"
                        header={t("provider_summary.column_productName")}
                        body={productNameBodyTemplate}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterMatchMode="contains"
                        filterPlaceholder={t("provider_summary.column_productName_filter")}></Column>
                    <Column
                        field="providerName"
                        header={t("provider_summary.column_providerName")}
                        filter
                        sortable
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_providerName_filter")}></Column>
                    <Column
                        field="shopName"
                        header={t("provider_summary.shops")}
                        filter
                        filterElement={shopFilter}
                        sortable
                        style={{ minWidth: "150px" }}></Column>
                    <Column
                        field="ownPrice"
                        body={(row) => renderPrice(row, "ownPrice")}
                        header={t("provider_summary.column_ownPrice")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_ownPrice_filter")}></Column>
                    <Column
                        field="ownTotalPrice"
                        body={(row) => renderPrice(row, "ownTotalPrice")}
                        header={t("provider_summary.column_ownTotalPrice")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_ownTotalPrice_filter")}></Column>
                    <Column
                        field="foreignPrice"
                        body={(row) => renderPrice(row, "foreignPrice")}
                        header={t("provider_summary.column_foreignPrice")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_foreignPrice_filter")}></Column>
                    <Column
                        field="foreignTotalPrice"
                        body={(row) => renderPrice(row, "foreignTotalPrice")}
                        header={t("provider_summary.column_foreignTotalPrice")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_foreignTotalPrice_filter")}></Column>
                    <Column
                        field="priceDifference"
                        body={priceDifferenceTemplate}
                        header={t("provider_summary.column_priceDifference")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_priceDifference_filter")}></Column>
                    <Column
                        field="stockString"
                        header={t("provider_summary.column_stock")}
                        sortable
                        filter
                        style={{ minWidth: "150px" }}
                        filterPlaceholder={t("provider_summary.column_stock_filter")}></Column>
                    <Column field="createDate" header={t("provider_summary.column_createDate")} sortable></Column>
                </DataTable>
            </div>
            <div
                className="modal fade"
                id="createProviderSummaryEntriesExportDialog"
                tabIndex="-1"
                role="dialog"
                aria-labelledby="createProviderSummaryEntriesExportDialogLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="createProviderSummaryEntriesExportDialogLabel">
                                Export
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body">
                            {t("pricetable.export_dialog.question", { countProducts: totalRecords })}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                {t("pricetable.export_dialog.cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={() => {
                                    triggerProviderSummaryEntriesExport()
                                }}>
                                {t("pricetable.export_dialog.create")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
